import React, { useMemo, useState } from 'react';
import Page from 'components/Page';
import { Button, Col, Result, Row } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import useCustomer from 'hooks/useCustomer';
import Hero from 'components/Hero';
import { CREATE_DATA_SOURCE, EDIT_LICENSE } from 'shared/constants/permissions';
import { FormattedMessage } from 'react-intl';
import globalMessages from 'translations/messages';
import useUser from 'hooks/useUser';
import { useHistory } from 'react-router';
import { useQuery } from 'hooks/apollo';
import { gql } from '@apollo/client';
import { isLicenseTrial } from 'shared/utils/license';
import CreateDataSource from './CreateDataSource';
import DataSourceCard from './DataSourceCard';

const extraFields = `
  isDemo
  dataSources {
    id
    title
    type
    activeLicense {
      id
      title
      startDate
      endDate
      renewalDate
      interval
      license {
        id
        title
      }
      modules {
        id
        module
        title
      }
    }
  }
`;

const DEMO_DATA_SOURCE_QUERY = gql`
  query demoDataSource {
    demoDataSource {
      id
      title
      type
      activeLicense {
        id
        title
        startDate
        endDate
        renewalDate
        license {
          id
          title
        }
        modules {
          id
          module
          title
        }
      }
    }
  }
`;

function sortDatasources(dataSources, hasDataSourceAccess) {
  return dataSources?.toSorted((a, b) => {
    // Sort by access (hasDataSourceAccess)
    if (hasDataSourceAccess(a.id) && !hasDataSourceAccess(b.id)) return -1;
    if (!hasDataSourceAccess(a.id) && hasDataSourceAccess(b.id)) return 1;

    // Sort by has active license
    const hasActiveLicenseA = Boolean(a.activeLicense);
    const hasActiveLicenseB = Boolean(b.activeLicense);
    if (hasActiveLicenseA && !hasActiveLicenseB) return -1;
    if (!hasActiveLicenseA && hasActiveLicenseB) return 1;

    // Sort by title
    return a.title.localeCompare(b.title);
  });
}

const DataSources = () => {
  const { hasDataSourceAccess, hasDataSourcePermission, refetch } = useUser();
  const { customer, hasCustomerPermission, loading } = useCustomer({ extraFields });
  const history = useHistory();
  const [createDataSource, setCreateDataSource] = useState(false);

  const onDataSourceCreated = (id) => {
    setCreateDataSource(false);
    refetch();
    history.push(`/data-sources/${id}`);
  };

  const canCreate = hasCustomerPermission(CREATE_DATA_SOURCE);

  const dataSources = useMemo(() => sortDatasources(customer?.dataSources, hasDataSourceAccess), [customer?.dataSources, hasDataSourceAccess]);

  const hasTrialDataSources = dataSources?.some(dataSource => isLicenseTrial(dataSource.activeLicense));

  const demoDataSourceResult = useQuery(DEMO_DATA_SOURCE_QUERY, { skip: !hasTrialDataSources });
  const demoDataSource = demoDataSourceResult?.data?.demoDataSource;

  return (
    <Page loading={loading}>
      <Page.Header
        title={<FormattedMessage {...globalMessages.dataSources} />}
        extra={canCreate && dataSources?.length > 0 && <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateDataSource(true)} className="create-data-source">Create data source</Button>}
      />

      {dataSources?.length ? (
        <Row gutter={[16, 16]}>
          {dataSources.map(dataSource => (
            <Col span={24} key={dataSource.id}>
              <DataSourceCard dataSource={dataSource} hasAccess={hasDataSourceAccess(dataSource.id)} canEditLicense={hasDataSourcePermission(dataSource.id, EDIT_LICENSE)} />
            </Col>
          ))}
          {demoDataSource && (
            <Col span={24}>
              <DataSourceCard dataSource={demoDataSource} hasAccess isDemo />
            </Col>
          )}
        </Row>

      ) : (
        <Hero>
          {customer ? (
            <Result
              title="Create your first data source"
              subTitle={(
                <p>
                  Data sources are where your data comes from, for example your website or your customers.
                  <br />
                  You can create multiple data sources and connect them to different websites.
                </p>
              )}
              extra={canCreate ? (
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateDataSource(true)}>
                  Create data source
                </Button>
              ) : <p>You don&apos;t have permission to create data sources for this account.</p>}
            />
          ) : (
            <Result
              subTitle={<FormattedMessage {...globalMessages.selectCustomer} />}
            />
          )}
        </Hero>
      )}

      <CreateDataSource open={createDataSource} onCancel={() => setCreateDataSource(false)} onDone={onDataSourceCreated} />
    </Page>
  );
};

export default DataSources;
